import Image from "next/image";

import { FormProvider } from "react-hook-form";

import { useOnboardingNavigation } from "@/components/hooks/useOnboardingNavigation";
import ReboltLoader from "@/components/icons/ReboltLoader";
import { onboardingSteps } from "@/components/layouts/ReboltOnboardingLayout";
import {
  AppStoreReview,
  ReboltAppCTA,
  ReboltMessage,
  ReboltTestimonial,
  Statistic,
} from "@/components/onboarding/DesktopWidgets";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { ReactNode } from "react";
import type { OnboardingFlowProps, OnboardingSteps } from "@/types/onboarding";

export function OnboardingFlow({ steps = onboardingSteps, className }: OnboardingFlowProps) {
  const navigation = useOnboardingNavigation({
    steps,
  });

  const currentStep = steps[navigation.currentStepIndex];

  const StepComponent = currentStep?.component || null;

  // show loading state while auth is being determined
  if (!navigation.isAuthLoaded) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 z-10 flex items-center justify-center bg-clay-100">
        <ReboltLoader className="w-16 h-16" color="rebolt-500" />
      </div>
    );
  }

  if (!currentStep || !StepComponent) {
    // fallback if somehow the user ends up on an invalid step or one without a component - reset to step 0
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 z-10 flex items-center justify-center transition-colors duration-200 bg-ash-100 text-ash-900">
        <div className="flex flex-col items-center justify-center gap-4">
          <p className="text-ash-800">Step not found</p>
          <Button
            type="button"
            variant="rebolt-secondary"
            className="tracking-tight"
            size="lg"
            onClick={() => navigation.goToNamedStep("company-search")}
          >
            Restart
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...navigation.form}>
      <div
        className={cn(
          "fixed top-0 lg:top-2.5 left-0 right-0 bottom-0 z-10 flex items-center transition-colors duration-200 bg-clay-100 text-ash-900",
          className
        )}
      >
        <div className="absolute top-0 lg:-top-2.5 left-0 right-0 h-2.5 bg-ash-300">
          <div
            className="bg-rebolt-500 h-2.5 transition-all duration-300"
            style={{ width: `${((navigation.currentStepIndex + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="w-full h-full grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-2 overflow-y-auto">
          <div
            className={cn(
              "col-span-1 lg:col-span-3 xl:col-span-1 flex flex-col items-start justify-start relative overflow-y-auto"
            )}
          >
            <div className="py-6 pt-8 lg:pt-6 px-5 lg:px-40">
              <Image
                src="/rebolt-branding/rebolt-full-dark.png"
                alt="Rebolt Logo"
                height={40}
                width={0}
                sizes="100vw"
                className="h-6 sm:h-10 w-auto"
              />
            </div>

            <StepComponent
              form={navigation.form}
              onNext={navigation.nextStep}
              onBack={navigation.previousStep}
              isLoading={navigation.isSubmitting}
              canGoNext={navigation.currentStepIndex < steps.length - 1}
              canGoBack={navigation.currentStepIndex > 0 && currentStep.allowBack !== false}
              isOptional={currentStep.isOptional || false}
              isSubmitting={navigation.isSubmitting}
              goToNamedStep={navigation.goToNamedStep}
              goToStep={navigation.goToStep}
            />
          </div>

          <SideImageWidget steps={steps} currentStepIndex={navigation.currentStepIndex} />
        </div>

        {navigation.devMode && (
          <DevToolbar
            currentStepIndex={navigation.currentStepIndex}
            totalSteps={steps.length}
            stepId={currentStep.id}
            onPrev={navigation.previousStep}
            onNext={navigation.nextStep}
            onGoToStep={navigation.goToStep}
          />
        )}
      </div>
    </FormProvider>
  );
}

function SideImageWidget({ steps, currentStepIndex }: { steps: OnboardingSteps; currentStepIndex: number }) {
  const currentStep = steps[currentStepIndex];

  switch (currentStep.id) {
    case "company-search":
      return (
        <DesktopImageContainer image={currentStep.image}>
          <ReboltTestimonial
            title="50% Lead increase"
            quote="“Rebolt is incredibly affordable for what it delivers. It streamlined our marketing, significantly increased leads, and simplified day-to-day operations. If you're on the fence, don’t wait—pull the trigger.”"
            author={{
              name: "Todd Porter",
              company: "Apex Junk Removal",
              image: "/onboarding/todd.png",
            }}
          />
        </DesktopImageContainer>
      );

    case "company-selection":
      return (
        <DesktopImageContainer image={currentStep.image}>
          <Statistic
            body="78% of high-performing field service pros use AI, and 83% use streamline operations with workflow automation."
            source="salesforce.com"
          />
        </DesktopImageContainer>
      );

    case "company-details-basics":
      return (
        <DesktopImageContainer image={currentStep.image}>
          <ReboltTestimonial
            title="AI-assisted review responses"
            quote="“That's an incredible app. I just responded to 35 google reviews that I didn't have to even write.”"
            author={{
              name: "Kristen Lippert",
              company: "Topline Construction",
              image: "/onboarding/kristen.png",
            }}
          />
        </DesktopImageContainer>
      );
    case "company-details-statistics":
      return (
        <DesktopImageContainer image={currentStep.image}>
          <ReboltMessage
            title="Be more efficient"
            body="Get 12 hours back by letting Rebolt handle the quoting, invoicing, scheduling, and the admin."
          />
        </DesktopImageContainer>
      );
    case "rebolt-goals":
      return (
        <DesktopImageContainer image={currentStep.image}>
          <AppStoreReview
            title="Game Changer For My Business"
            quote="“This app made running my businesses so much easier. It consolidated my lead and review management to one platform along with all my social media posting.”"
            date="11/12/2025"
          />
        </DesktopImageContainer>
      );

    case "final-review":
      return (
        <DesktopImageContainer image={currentStep.image}>
          <ReboltAppCTA
            title="Get 7 extra days!"
            description="Download the Rebolt app and unlock an extra week in your trial! Take your Rebolt tools with you, wherever you go."
          />
        </DesktopImageContainer>
      );

    default:
      return (
        <div
          className="lg:col-span-2 xl:col-span-1 hidden lg:flex lg:items-center lg:justify-center bg-center bg-cover bg-no-repeat relative"
          style={{ backgroundImage: `url(${currentStep.image})`, opacity: 0.25 }}
        />
      );
  }
}

function DesktopImageContainer({ children, image }: { children: ReactNode; image?: string }) {
  return (
    <div
      className="lg:col-span-2 xl:col-span-1 hidden lg:flex lg:items-center lg:justify-center bg-center bg-cover bg-no-repeat relative"
      style={image ? { backgroundImage: `url(${image})` } : {}}
    >
      <div className="absolute inset-0 bg-white/70" />
      <div className="relative z-10 flex items-center justify-center w-full h-full md:mx-6 xl:mx-24">{children}</div>
    </div>
  );
}

function DevToolbar({
  currentStepIndex,
  totalSteps,
  stepId,
  onPrev,
  onNext,
  onGoToStep,
}: {
  currentStepIndex: number;
  totalSteps: number;
  stepId: string;
  onPrev: () => void;
  onNext: () => Promise<void>;
  onGoToStep: (step: number) => void;
}) {
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-xl bg-ash-900 px-4 py-2 text-xs text-white shadow-lg font-mono">
      <span className="text-amber-400 font-semibold mr-1">DEV</span>
      <button
        type="button"
        onClick={onPrev}
        disabled={currentStepIndex === 0}
        className="rounded bg-ash-700 px-2 py-1 hover:bg-ash-600 disabled:opacity-30"
      >
        Prev
      </button>
      <select
        value={currentStepIndex}
        onChange={(e) => onGoToStep(Number(e.target.value))}
        className="rounded bg-ash-700 px-2 py-1 text-xs text-white"
      >
        {Array.from({ length: totalSteps }, (_, i) => (
          <option key={i} value={i}>
            {i}
          </option>
        ))}
      </select>
      <span className="text-ash-400 max-w-32 truncate">{stepId}</span>
      <button
        type="button"
        onClick={() => void onNext()}
        disabled={currentStepIndex >= totalSteps - 1}
        className="rounded bg-ash-700 px-2 py-1 hover:bg-ash-600 disabled:opacity-30"
      >
        Next
      </button>
    </div>
  );
}
